/**
 * ReactionChips — the reactions under a bubble (PR-4, 2026-09-09): one chip
 * per emoji with its count; the viewer's own reactions read as selected; a
 * click toggles; the tooltip and accessible label name who reacted. Sits in
 * its own row under the bubble (the MessageTime meta row is nowrap).
 */
import React from 'react';
import type { ReactionSummaryEntry } from '../../services/messageReactions/reactionModel';
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';

export interface ReactionChipsProps {
  reactions: ReadonlyArray<ReactionSummaryEntry>;
  /** uid → display name for the tooltip. */
  labelFor: (uid: string) => string;
  onToggle: (emoji: string) => void;
  align?: 'left' | 'right';
  /** False while the message cannot take a reaction yet (not delivered). */
  canReact?: boolean;
}

export function reactionChipLabel(entry: ReactionSummaryEntry, labelFor: (uid: string) => string, lang: 'en' | 'es'): string {
  const names = entry.uids.map(labelFor).filter(Boolean);
  const who = names.join(', ');
  if (lang === 'es') return `${entry.emoji} ${entry.count} — ${who}`;
  return `${entry.emoji} ${entry.count} — ${who}`;
}

export function ReactionChips({ reactions, labelFor, onToggle, align = 'left', canReact = true }: ReactionChipsProps): React.ReactElement | null {
  if (!reactions || reactions.length === 0) return null;
  const lang = getUserUiLanguage() === 'es' ? 'es' : 'en';
  return (
    <div
      data-reaction-chips=""
      role="group"
      aria-label={lang === 'es' ? 'reacciones' : 'reactions'}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        gap: 4,
        marginTop: 6,
        userSelect: 'none',
      }}
    >
      {reactions.map((entry) => {
        const label = reactionChipLabel(entry, labelFor, lang);
        return (
          <button
            key={entry.emoji}
            type="button"
            data-reaction-chip={entry.emoji}
            data-reaction-count={entry.count}
            data-reaction-mine={entry.mine ? 'true' : undefined}
            aria-pressed={entry.mine}
            aria-label={label}
            title={label}
            disabled={!canReact}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(entry.emoji);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '2px 8px',
              borderRadius: 999,
              border: entry.mine ? '1px solid rgba(138,109,47,0.7)' : '1px solid rgba(0,0,0,0.12)',
              background: entry.mine ? 'rgba(138,109,47,0.14)' : 'rgba(255,255,255,0.85)',
              color: 'var(--brand-black)',
              fontSize: '0.78rem',
              lineHeight: 1.2,
              cursor: canReact ? 'pointer' : 'default',
            }}
          >
            <span aria-hidden="true">{entry.emoji}</span>
            <span aria-hidden="true" style={{ fontWeight: 600 }}>{entry.count}</span>
          </button>
        );
      })}
    </div>
  );
}

export default ReactionChips;
